/**
 * TypeScript interfaces and types for Aircraft ESAL (Equivalent Single Axle Load) calculations
 * Adapted from ESALTypes.ts for Airport project type - uses "aircraft movements" instead of "vehicles"
 */

import { AircraftConfigurationService, AircraftConfiguration as ServiceAircraftConfiguration } from '../../../../services/AircraftConfigurationService';

/** Configuration for loading scenarios and weight distribution */
export interface LoadingScenarioConfig {
    /** Empty load multiplier (default: 0.1 = 10%) */
    emptyMultiplier: number;
    /** Half full load multiplier (default: 0.5 = 50%) */
    halfFullMultiplier: number;
    /** Overload multiplier (default: 1.15 = 115%) */
    overloadMultiplier: number;
    /** Empty scenario percentage (default: 20%) */
    emptyPercentage: number;
    /** Half full scenario percentage (default: 20%) */
    halfFullPercentage: number;
    /** Fully loaded scenario percentage (default: 50%) */
    fullyLoadedPercentage: number;
    /** Overloaded scenario percentage (default: 10%) */
    overloadedPercentage: number;
}

/** Default loading scenario configuration based on Excel patterns */
export const DEFAULT_LOADING_CONFIG: LoadingScenarioConfig = {
    emptyMultiplier: 0.1, // 10% of design load
    halfFullMultiplier: 0.5, // 50% of design load
    overloadMultiplier: 1.15, // 115% of design load
    emptyPercentage: 20, // 20% of time
    halfFullPercentage: 20, // 20% of time
    fullyLoadedPercentage: 50, // 50% of time
    overloadedPercentage: 10, // 10% of time
};

/** Base aircraft configuration data from Excel */
export interface AircraftConfiguration {
    /** Aircraft identifier (1-8) */
    id: number;
    /** Display name (Aircraft1, Aircraft2, etc.) */
    name: string;
    /** Number of axles (fixed from Excel) */
    axles: number;
    /** Number of steering axles */
    steeringAxles: number;
    /** Number of single axles */
    singleAxles: number;
    /** Number of tandem axles */
    tandemAxles: number;
    /** Number of tridem axles */
    tridemAxles: number;
    /** Steering axle load in kN */
    steeringAxleLoad: number;
    /** Single axle load in kN */
    singleAxleLoad: number;
    /** Tandem axle load in kN */
    tandemAxleLoad: number;
    /** Tridem axle load in kN */
    tridemAxleLoad: number;
    /** Percentage of total load on steering axles */
    steeringLoadPercentage: number;
    /** Percentage of total load on single axles */
    singleLoadPercentage: number;
    /** Percentage of total load on tandem axles */
    tandemLoadPercentage: number;
    /** Percentage of total load on tridem axles */
    tridemLoadPercentage: number;
    /** Design total weight in kN (fixed from Excel) */
    designTotalLoad: number;
    /** Excel cell reference for Direction 1 aircraft movements */
    direction1Cell: string;
    /** Excel cell reference for Direction 2 aircraft movements */
    direction2Cell: string;
    /** Path to aircraft image (e.g. Aircraft1.png) */
    imageSrc: string;
}

/** User-editable aircraft data */
export interface AircraftData {
    /** Aircraft identifier (1-8) */
    id: number;
    /** Whether aircraft is selected for calculations */
    selected: boolean;
    /** Display name (Aircraft1, Aircraft2, etc.) */
    name: string;
    /** Number of axles (fixed from Excel) */
    axles: number;
    /** Number of vehicles per day on Runway 1 / Direction 1 */
    runway1Vehicles: number;
    /** Number of vehicles per day on Runway 2 / Direction 2 */
    runway2Vehicles: number;
    /** User-defined total weight in kN (USERS TOTAL LOAD) - 0 means use design load */
    usersTotalLoad: number;
    /** Design total weight in kN (DESIGN TOTAL LOAD - from configuration) */
    designTotalLoad: number;
    /** Effective total weight used for calculations (users load if >0, else design load) */
    effectiveTotalLoad: number;
    /** User-editable number of steering axles (1-20) */
    steeringAxles: number;
    /** User-editable number of single axles (1-20) */
    singleAxles: number;
    /** User-editable number of tandem axles (1-20) */
    tandemAxles: number;
    /** User-editable number of tridem axles (1-20) */
    tridemAxles: number;
    /** User-editable percentage of total load on steering axles */
    steeringLoadPercentage: number;
    /** User-editable percentage of total load on single axles */
    singleLoadPercentage: number;
    /** User-editable percentage of total load on tandem axles */
    tandemLoadPercentage: number;
    /** User-editable percentage of total load on tridem axles */
    tridemLoadPercentage: number;
    /** Steering axle load in kN (editable) */
    steeringAxleLoad: number;
    /** Single axle load in kN (editable) */
    singleAxleLoad: number;
    /** Tandem axle load in kN (editable) */
    tandemAxleLoad: number;
    /** Tridem axle load in kN (editable) */
    tridemAxleLoad: number;
    /** EALF for steering axle (calculated, not displayed) */
    ealfSteeringAxle: number;
    /** EALF for single axle (calculated, not displayed) */
    ealfSingleAxle: number;
    /** EALF for tandem axle (calculated, not displayed) */
    ealfTandemAxle: number;
    /** EALF for tridem axle (calculated, not displayed) */
    ealfTridemAxle: number;
    /** Sum of all EALF values per vehicle (calculated) */
    equivalentEalfPerVehicle: number;
    /** Calculated ESAL per day for runway 1 */
    esalRunway1: number;
    /** Calculated ESAL per day for runway 2 */
    esalRunway2: number;
    /** Calculated ESAL per day for this aircraft (total of both runways) */
    esalPerDay: number;
    /** Percentage of total movements */
    passagePercentage: number;
    /** Path to aircraft image (require result) */
    imageSrc: any;
    /** Validation status for axle count vs configuration */
    axleCountValid: boolean;
    /** Validation status for load percentages (must sum to 100%) */
    loadPercentagesValid: boolean;
}

/** EALF (Equivalent Axle Load Factor) calculation parameters */
export interface EALFParameters {
    /** Steering axle EALF */
    steeringAxle: number;
    /** Single axle EALF */
    singleAxle: number;
    /** Tandem axle EALF */
    tandemAxle: number;
    /** Tridem axle EALF */
    tridemAxle: number;
    /** Total equivalent EALF per aircraft */
    totalPerVehicle: number;
}

/** Summary calculation data displayed in right panel */
export interface SummaryCalculationData {
    /** Total number of aircraft movements N1 */
    totalPassages: number;
    /** Directional distribution factor (typically 0.5) */
    directionalDistribution: number;
    /** Lane distribution factor (typically 0.90) */
    laneDistribution: number;
    /** Analysis period in years (typically 12) */
    analysisPeriod: number;
    /** Year growth rate as percentage (typically 2.00%) */
    yearGrowthRate: number;
    /** Compound Traffic Growth Factor */
    compoundTrafficGrowthFactor: number;
    /** Total volume of traffic during analysis period (w'18) */
    totalVolumeTraffic: number;
    /** Traffic in design lane (w18 = DD * DL * w'18) */
    trafficDesignLane: number;
    /** User defined traffic in design lane (0 = use calculated) */
    userDefinedTrafficDesignLane: number;
}

/** Complete ESAL calculation state for aircraft */
export interface ESALCalculationStateAircraft {
    /** Array of aircraft data for all 8 aircraft */
    aircraft: AircraftData[];
    /** Summary calculation results */
    summaryData: SummaryCalculationData;
    /** Total ESAL from all selected aircraft */
    totalESAL: number;
    /** Loading state for calculations */
    isCalculating: boolean;
    /** Any calculation errors */
    calculationError: string | null;
}

/** Props for the main ESAL calculation modal for aircraft */
export interface EsalCalculationModalAirportProps {
    /** Whether the modal is open */
    open: boolean;
    /** Callback when modal should be closed */
    onClose: () => void;
    /** Callback when calculation is complete with result */
    onCalculationComplete: (result: number) => void;
}

/** Props for aircraft data grid component */
export interface AircraftDataGridProps {
    /** Array of aircraft data */
    aircraft: AircraftData[];
    /** Callback when aircraft data is updated */
    onAircraftUpdate: (updatedAircraft: AircraftData) => void;
    /** Whether the grid is in edit mode */
    isEditable: boolean;
    /** Optional project data for dynamic SN calculation */
    projectData?: any;
    /** Validity of each numeric cell, keyed `row:field`: the owner blocks its action while any key is invalid */
    onValidityChange?: (key: string, isValid: boolean) => void;
    /** A change puts every cell back on the stored value (owner reset, close, reopen) */
    resetKey?: string | number;
}

/** Props for calculations summary component for aircraft */
export interface CalculationsSummaryAircraftProps {
    /** Summary calculation data to display */
    summaryData: SummaryCalculationData;
    /** Total ESAL from all aircraft */
    totalESAL: number;
    /** Loading state */
    isCalculating: boolean;
    /** Callback when summary calculation inputs change */
    onSummaryInputsChange?: (inputs: { dd: number; dl: number; analysisPeriod: number; growthRate: number; trafficDesignLane: number }) => void;
    /** Validity of each numeric cell, keyed `row:field`: the owner blocks its action while any key is invalid */
    onValidityChange?: (key: string, isValid: boolean) => void;
    /** A change puts every cell back on the stored value (owner reset, close, reopen) */
    resetKey?: string | number;
}

/** Excel formula calculation input parameters for aircraft */
export interface FormulaCalculationInputAircraft {
    /** Aircraft weight in kN */
    totalWeight: number;
    /** Number of aircraft movements per day */
    vehiclesPerDay: number;
    /** Percentage of total movements */
    passagePercentage: number;
    /** Number of axles */
    axles: number;
    /** Steering axle load in kN */
    steeringAxleLoad: number;
    /** Single axle load in kN */
    singleAxleLoad: number;
    /** Tandem axle load in kN */
    tandemAxleLoad: number;
    /** Tridem axle load in kN */
    tridemAxleLoad: number;
}

/**
 * ESAL Constants - Loaded from centralized esal-constants.json
 *
 * MIGRATION: Eliminates 150+ lines of duplicate constants across ESALTypes.ts and ESALTypesAircraft.ts
 * Single source of truth: src/app/data/esal-constants.json
 * Service loader: ESALConstantsService.getConstants('airport')
 *
 * This transformation maintains backward compatibility with existing UPPER_SNAKE_CASE property names
 * while loading from the centralized camelCase service.
 */
import { ESALConstantsService } from '../../../../services/ESALConstantsService';

// Load constants for Airport project type
const _airportConstants = ESALConstantsService.getConstants('airport');

/** Constants from Excel calculations (loaded from esal-constants.json) */
export const ESAL_CONSTANTS = {
    /** Structural Number (SN) */
    STRUCTURAL_NUMBER: _airportConstants.structuralNumber,
    /** Present Serviceability (Pt) */
    PRESENT_SERVICEABILITY: _airportConstants.presentServiceability,
    /** Load conversion factor (kN to kips) */
    KN_TO_KIPS: _airportConstants.knToKips,
    /** Default directional distribution factor */
    DEFAULT_DD: _airportConstants.defaultDD,
    /** Default lane distribution factor */
    DEFAULT_DL: _airportConstants.defaultDL,
    /** Default analysis period (years) */
    DEFAULT_ANALYSIS_PERIOD: _airportConstants.defaultAnalysisPeriod,
    /** Default year growth rate (%) */
    DEFAULT_GROWTH_RATE: _airportConstants.defaultGrowthRate,
    /** Beta factor base value - AASHTO coefficient */
    BETA_BASE: _airportConstants.betaBase,
    /** Beta factor coefficient - AASHTO coefficient */
    BETA_COEFFICIENT: _airportConstants.betaCoefficient,
    /** Log base for calculations - AASHTO coefficient */
    LOG_BASE: _airportConstants.logBase,
    /** AASHTO formula exponents and coefficients */
    AASHTO_EXPONENTS: {
        /** Load exponent in beta calculation (3.23) */
        LOAD_EXPONENT: _airportConstants.aashtoExponents.loadExponent,
        /** Structural number exponent in beta calculation (5.19) */
        SN_EXPONENT: _airportConstants.aashtoExponents.snExponent,
        /** Load exponent in EALF calculation (4.79) */
        EALF_LOAD_EXPONENT: _airportConstants.aashtoExponents.ealfLoadExponent,
        /** Axle constant exponent in EALF calculation (4.33) */
        EALF_AXLE_EXPONENT: _airportConstants.aashtoExponents.ealfAxleExponent,
    },
    /** AASHTO axle type constants */
    AXLE_CONSTANTS: {
        /** Steering axle constant */
        STEERING: _airportConstants.axleConstants.steering,
        /** Single axle constant */
        SINGLE: _airportConstants.axleConstants.single,
        /** Tandem axle constant */
        TANDEM: _airportConstants.axleConstants.tandem,
        /** Tridem axle constant */
        TRIDEM: _airportConstants.axleConstants.tridem,
    },
} as const;

/**
 * Helper function to dynamically require aircraft image
 * Converts image path from JSON to webpack require() call
 */
function requireAircraftImage(imagePath: string): string {
    // imagePath format: "aircraft/Aircraft1-t.png"
    // Convert to require path: "../../../../images/aircraft/Aircraft1-t.png"
    return require(`../../../../images/${imagePath}`);
}

/**
 * Convert service configuration to local interface format
 * Adds imageSrc by dynamically requiring the image based on imagePath
 */
function convertToLocalConfig(serviceConfig: ServiceAircraftConfiguration): AircraftConfiguration {
    return {
        id: serviceConfig.id,
        name: serviceConfig.name,
        axles: serviceConfig.axles,
        steeringAxles: serviceConfig.steeringAxles,
        singleAxles: serviceConfig.singleAxles,
        tandemAxles: serviceConfig.tandemAxles,
        tridemAxles: serviceConfig.tridemAxles,
        steeringAxleLoad: serviceConfig.steeringAxleLoad,
        singleAxleLoad: serviceConfig.singleAxleLoad,
        tandemAxleLoad: serviceConfig.tandemAxleLoad,
        tridemAxleLoad: serviceConfig.tridemAxleLoad,
        steeringLoadPercentage: serviceConfig.steeringLoadPercentage,
        singleLoadPercentage: serviceConfig.singleLoadPercentage,
        tandemLoadPercentage: serviceConfig.tandemLoadPercentage,
        tridemLoadPercentage: serviceConfig.tridemLoadPercentage,
        designTotalLoad: serviceConfig.designTotalLoad,
        direction1Cell: serviceConfig.direction1Cell,
        direction2Cell: serviceConfig.direction2Cell,
        imageSrc: requireAircraftImage(serviceConfig.imagePath),
    };
}

/**
 * Default aircraft configurations based on Excel data from TRAFFIC LOAD FAA.txt
 * Loaded from centralized JSON configuration via AircraftConfigurationService
 *
 * Migration note: Replaces hardcoded array (lines 298-509) with service-backed data
 * Preserves backward compatibility by maintaining same interface structure
 */
export const DEFAULT_AIRCRAFT_CONFIGURATIONS: AircraftConfiguration[] = AircraftConfigurationService.getAvailableAircraftIds().map(id => {
    const config = AircraftConfigurationService.getConfiguration(id);
    if (!config) {
        throw new Error(`Aircraft configuration not found for id ${id}`);
    }
    return convertToLocalConfig(config);
});

/** Default aircraft data values from Excel analysis - Updated with TRAFFIC LOAD FAA.txt values */
export const DEFAULT_AIRCRAFT_VALUES: Partial<AircraftData>[] = [
    {
        // Aircraft1 (BOMBARDIER GLOBAL 6000) - from Excel C10=1, C65=1 movements, USER LOAD=454, DESIGN=454
        id: 1,
        selected: true,
        runway1Vehicles: 1, // C10 from Excel
        runway2Vehicles: 1, // C65 from Excel
        usersTotalLoad: 454, // Match design load from Excel
        passagePercentage: 100,
        steeringLoadPercentage: 10, // F12 from Excel
        singleLoadPercentage: 90, // G12 from Excel
        tandemLoadPercentage: 0, // H12 from Excel
        tridemLoadPercentage: 0, // I12 from Excel
    },
    {
        // Aircraft2 (ATR 42) - from Excel C15=1, C70=1 movements, USER LOAD=167, DESIGN=167
        id: 2,
        selected: true,
        runway1Vehicles: 1, // C15 from Excel
        runway2Vehicles: 1, // C70 from Excel
        usersTotalLoad: 167, // Match design load from Excel
        passagePercentage: 100,
        steeringLoadPercentage: 10, // F17 from Excel
        singleLoadPercentage: 90, // G17 from Excel
        tandemLoadPercentage: 0, // H17 from Excel
        tridemLoadPercentage: 0, // I17 from Excel
    },
    {
        // Aircraft3 (AIRBUS A330) - from Excel C20=1, C75=1 movements, USER LOAD=2330, DESIGN=2330
        id: 3,
        selected: true,
        runway1Vehicles: 1, // C20 from Excel
        runway2Vehicles: 1, // C75 from Excel
        usersTotalLoad: 2330, // Match design load from Excel
        passagePercentage: 100,
        steeringLoadPercentage: 10, // F22 from Excel
        singleLoadPercentage: 45, // G22 from Excel
        tandemLoadPercentage: 45, // H22 from Excel
        tridemLoadPercentage: 0, // I22 from Excel
    },
    {
        // Aircraft4 (BOEING 737-300) - from Excel C25=175, C80=175 movements, USER LOAD=635, DESIGN=635
        id: 4,
        selected: true,
        runway1Vehicles: 1, // C25 from Excel
        runway2Vehicles: 1, // C80 from Excel (assume same for both runways)
        usersTotalLoad: 635, // Match design load from Excel
        passagePercentage: 100,
        steeringLoadPercentage: 10, // F27 from Excel
        singleLoadPercentage: 90, // G27 from Excel
        tandemLoadPercentage: 0, // H27 from Excel
        tridemLoadPercentage: 0, // I27 from Excel
    },
    {
        // Aircraft5 (BOEING 767-400 ER) - from Excel C30=7, C85=7 movements, USER LOAD=2050, DESIGN=2050
        id: 5,
        selected: true,
        runway1Vehicles: 1, // C30 from Excel
        runway2Vehicles: 1, // C85 from Excel
        usersTotalLoad: 2050, // Match design load from Excel
        passagePercentage: 100,
        steeringLoadPercentage: 10, // F32 from Excel
        singleLoadPercentage: 45, // G32 from Excel
        tandemLoadPercentage: 45, // H32 from Excel
        tridemLoadPercentage: 0, // I32 from Excel
    },
    {
        // Aircraft6 (BOEING 777-300 ER) - from Excel C35=7, C90=7 movements, USER LOAD=3520, DESIGN=3520
        id: 6,
        selected: true,
        runway1Vehicles: 1, // C35 from Excel
        runway2Vehicles: 1, // C90 from Excel
        usersTotalLoad: 3520, // Match design load from Excel
        passagePercentage: 100,
        steeringLoadPercentage: 10, // F37 from Excel
        singleLoadPercentage: 45, // G37 from Excel
        tandemLoadPercentage: 45, // H37 from Excel
        tridemLoadPercentage: 0, // I37 from Excel
    },
    {
        // Aircraft7 (BOEING B747 JUMBO) - from Excel C40=1, C95=1 movements, USER LOAD=4425, DESIGN=4425
        id: 7,
        selected: true,
        runway1Vehicles: 1, // C40 from Excel
        runway2Vehicles: 1, // C95 from Excel
        usersTotalLoad: 4425, // Match design load from Excel
        passagePercentage: 100,
        steeringLoadPercentage: 10, // F42 from Excel
        singleLoadPercentage: 45, // G42 from Excel
        tandemLoadPercentage: 45, // H42 from Excel
        tridemLoadPercentage: 0, // I42 from Excel
    },
    {
        // Aircraft8 (Lockheed C-5 Galaxy) - from Excel C45=1, C100=1 movements, USER LOAD=3810, DESIGN=3810
        id: 8,
        selected: true,
        runway1Vehicles: 1, // C45 from Excel
        runway2Vehicles: 1, // C100 from Excel
        usersTotalLoad: 3810, // Match design load from Excel
        passagePercentage: 100,
        steeringLoadPercentage: 10, // F47 from Excel
        singleLoadPercentage: 45, // G47 from Excel
        tandemLoadPercentage: 45, // H47 from Excel
        tridemLoadPercentage: 0, // I47 from Excel
    },
    {
        // Aircraft9 (AIRBUS A 380) - from Excel C50=1, C105=1 movements, USER LOAD=5600, DESIGN=5600
        id: 9,
        selected: true,
        runway1Vehicles: 1, // C50 from Excel
        runway2Vehicles: 1, // C105 from Excel
        usersTotalLoad: 5600, // Match design load from Excel
        passagePercentage: 100,
        steeringLoadPercentage: 10, // F52 from Excel
        singleLoadPercentage: 45, // G52 from Excel
        tandemLoadPercentage: 45, // H52 from Excel
        tridemLoadPercentage: 0, // I52 from Excel
    },
    {
        // Aircraft10 (Antonov An-225 Mriya) - from Excel C55=1, C110=1 movements, USER LOAD=6400, DESIGN=6400
        id: 10,
        selected: true,
        runway1Vehicles: 1, // C55 from Excel
        runway2Vehicles: 1, // C110 from Excel
        usersTotalLoad: 6400, // Match design load from Excel
        passagePercentage: 100,
        steeringLoadPercentage: 10, // F57 from Excel
        singleLoadPercentage: 45, // G57 from Excel
        tandemLoadPercentage: 45, // H57 from Excel
        tridemLoadPercentage: 0, // I57 from Excel
    },
];

/**
 * Inputs for CalculationsSummaryAircraft onSummaryInputsChange callback
 */
export type CalculationsSummaryInputsAircraft = {
    dd: number;
    dl: number;
    analysisPeriod: number;
    growthRate: number;
    trafficDesignLane: number;
};
